import { callFunctionRaw } from "@/lib/callFunction";

const FIREBASE_SDK_VERSION = "10.13.2";
const AFRIMED_PUSH_ICON = "/afrimed-icon-pwa-192x192.png";
const APP_IMPORT_URL = `https://www.gstatic.com/firebasejs/${FIREBASE_SDK_VERSION}/firebase-app.js`;
const MESSAGING_IMPORT_URL = `https://www.gstatic.com/firebasejs/${FIREBASE_SDK_VERSION}/firebase-messaging.js`;
const PUSH_DEBUG_PREFIX = "[AfriMed Push]";
const SERVICE_WORKER_DIAGNOSTIC_TIMEOUT_MS = 8000;

let foregroundMessageUnsubscribe = null;

function emitPushDiagnosticEvent(detail = {}) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent("afrimed:push-diagnostic", {
        detail: {
          at: new Date().toISOString(),
          ...detail,
        },
      })
    );
  } catch {
    // Diagnostics must never affect notification delivery.
  }
}

function pushDebug(label, details = {}) {
  if (typeof console === "undefined") return;
  try {
    console.info(PUSH_DEBUG_PREFIX, label, details);
  } catch {
    // Debug logging must never affect notification delivery.
  }
}

function summarizePayload(payload = {}) {
  const hasNotification = Boolean(payload?.notification);
  const hasData = Boolean(payload?.data);
  return {
    messageId: payload?.messageId || null,
    title: payload?.notification?.title || null,
    type: payload?.data?.type || null,
    source: payload?.data?.source || null,
    hasNotification,
    hasData,
    payloadMode: hasNotification && hasData
      ? "notification_plus_data"
      : hasNotification
        ? "notification_only"
        : hasData
          ? "data_only"
          : "unknown",
  };
}

function summarizePushError(error) {
  const message = error?.message || String(error || "unknown_error");
  const code = error?.code || "";
  const normalized = `${code} ${message}`.toLowerCase();
  let reason = "fcm_token_failed";

  if (normalized.includes("permission") || normalized.includes("denied")) {
    reason = "push_permission_denied";
  } else if (normalized.includes("unsupported")) {
    reason = "firebase_messaging_unsupported";
  } else if (normalized.includes("vapid")) {
    reason = "vapid_key_failed";
  } else if (normalized.includes("service worker")) {
    reason = "service_worker_registration_failed";
  }

  return { code, message, reason };
}

export function getFirebasePushConfig() {
  const config = {
    apiKey: String(import.meta.env.VITE_FIREBASE_API_KEY || "").trim(),
    authDomain: String(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "").trim(),
    projectId: String(import.meta.env.VITE_FIREBASE_PROJECT_ID || "").trim(),
    storageBucket: String(import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "").trim(),
    messagingSenderId: String(import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "").trim(),
    appId: String(import.meta.env.VITE_FIREBASE_APP_ID || "").trim(),
  };
  const vapidKey = String(import.meta.env.VITE_FIREBASE_VAPID_KEY || "").trim();
  const missing = Object.entries(config)
    .filter(([, value]) => !value)
    .map(([key]) => key);
  if (!vapidKey) missing.push("vapidKey");
  return { config, vapidKey, missing };
}

export function isFirebasePushConfigured() {
  return getFirebasePushConfig().missing.length === 0;
}

async function loadFirebaseMessaging() {
  pushDebug("firebase sdk import start", { version: FIREBASE_SDK_VERSION });
  const [{ initializeApp, getApps }, { getMessaging, getToken, deleteToken, isSupported, onMessage }] =
    await Promise.all([
      import(/* @vite-ignore */ APP_IMPORT_URL),
      import(/* @vite-ignore */ MESSAGING_IMPORT_URL),
    ]);
  pushDebug("firebase sdk import success", { hasOnMessage: typeof onMessage === "function" });
  return { initializeApp, getApps, getMessaging, getToken, deleteToken, isSupported, onMessage };
}

async function registerServiceWorker(config) {
  if (!("serviceWorker" in navigator)) {
    pushDebug("service worker unsupported");
    throw new Error("Browser service workers are unavailable.");
  }
  const serviceWorkerUrl = new URL("/firebase-messaging-sw.js", window.location.origin);
  for (const [key, value] of Object.entries(config || {})) {
    if (value) serviceWorkerUrl.searchParams.set(key, value);
  }
  pushDebug("service worker registration start", { path: serviceWorkerUrl.pathname });
  const registration = await navigator.serviceWorker.register(
    serviceWorkerUrl.toString(),
    { scope: "/" }
  );
  const readyRegistration = await navigator.serviceWorker.ready;
  pushDebug("service worker registration ready", {
    scope: readyRegistration?.scope || registration?.scope || null,
    hasActiveWorker: Boolean(readyRegistration?.active || registration?.active),
  });
  readyRegistration.active?.postMessage({
    type: "AFRIMED_FIREBASE_CONFIG",
    config,
  });
  pushDebug("service worker config posted", {
    hasActiveWorker: Boolean(readyRegistration?.active),
  });
  return readyRegistration || registration;
}

function resolvePushClickUrl(payload = {}) {
  const candidate =
    payload?.fcmOptions?.link ||
    payload?.data?.url ||
    payload?.data?.click_action ||
    payload?.data?.link ||
    "/";
  try {
    return new URL(String(candidate || "/"), window.location.origin).toString();
  } catch {
    return `${window.location.origin}/`;
  }
}

function getNotificationDiagnosticOptions(body, extraData = {}) {
  return {
    body,
    icon: AFRIMED_PUSH_ICON,
    badge: AFRIMED_PUSH_ICON,
    tag: `afrimed-desktop-diagnostic-${Date.now()}`,
    data: {
      url: typeof window !== "undefined" ? window.location.href : "/",
      source: "afrimed_push_diagnostic",
      ...extraData,
    },
  };
}

export async function getFirebasePushRuntimeDiagnostics() {
  const { missing, vapidKey } = getFirebasePushConfig();
  const diagnostics = {
    configured: missing.length === 0,
    missing,
    vapidKeyPresent: Boolean(vapidKey),
    hasWindow: typeof window !== "undefined",
    hasNotificationApi: typeof window !== "undefined" && "Notification" in window,
    notificationPermission:
      typeof window !== "undefined" && "Notification" in window
        ? window.Notification.permission
        : "unavailable",
    hasServiceWorkerApi: typeof navigator !== "undefined" && "serviceWorker" in navigator,
    serviceWorkerReady: false,
    serviceWorkerScope: null,
    serviceWorkerActive: false,
    serviceWorkerController: false,
  };

  if (!diagnostics.hasServiceWorkerApi) return diagnostics;

  try {
    const registration = await navigator.serviceWorker.ready;
    diagnostics.serviceWorkerReady = Boolean(registration);
    diagnostics.serviceWorkerScope = registration?.scope || null;
    diagnostics.serviceWorkerActive = Boolean(registration?.active);
    diagnostics.serviceWorkerController = Boolean(navigator.serviceWorker.controller);
  } catch (error) {
    diagnostics.serviceWorkerError = error?.message || String(error || "unknown_error");
  }

  pushDebug("runtime diagnostics", diagnostics);
  return diagnostics;
}

export async function testDirectBrowserNotification() {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return {
      ok: false,
      reason: "browser_notifications_unavailable",
    };
  }

  const permission =
    window.Notification.permission === "granted"
      ? "granted"
      : await window.Notification.requestPermission();

  if (permission !== "granted") {
    return {
      ok: false,
      reason: permission || "permission_not_granted",
      permission,
    };
  }

  try {
    const notification = new window.Notification(
      "AfriMed™ desktop notification test",
      getNotificationDiagnosticOptions("Browser notifications are working for this device.", {
        diagnosticType: "direct_browser_notification",
      })
    );
    notification.onclick = () => {
      window.focus?.();
      notification.close();
    };
    pushDebug("direct browser notification success");
    return {
      ok: true,
      permission,
      method: "Notification",
    };
  } catch (error) {
    pushDebug("direct browser notification failure", {
      message: error?.message || String(error || "unknown_error"),
    });
    return {
      ok: false,
      permission,
      reason: "direct_notification_failed",
      detail: error?.message || String(error || "unknown_error"),
    };
  }
}

export async function testServiceWorkerNotification() {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return {
      ok: false,
      reason: "browser_notifications_unavailable",
    };
  }
  if (!("serviceWorker" in navigator)) {
    return {
      ok: false,
      reason: "service_worker_unavailable",
    };
  }

  const permission =
    window.Notification.permission === "granted"
      ? "granted"
      : await window.Notification.requestPermission();

  if (permission !== "granted") {
    return {
      ok: false,
      reason: permission || "permission_not_granted",
      permission,
    };
  }

  const { config, missing } = getFirebasePushConfig();
  if (missing.length) {
    return {
      ok: false,
      permission,
      reason: "missing_firebase_config",
      missing,
    };
  }

  try {
    const registration = await registerServiceWorker(config);
    const worker = registration?.active || navigator.serviceWorker.controller;
    if (!worker) {
      return {
        ok: false,
        permission,
        reason: "service_worker_not_active",
        scope: registration?.scope || null,
      };
    }

    const result = await new Promise((resolve) => {
      const channel = new MessageChannel();
      const timeout = window.setTimeout(() => {
        channel.port1.onmessage = null;
        resolve({
          ok: false,
          reason: "service_worker_notification_timeout",
          scope: registration?.scope || null,
        });
      }, SERVICE_WORKER_DIAGNOSTIC_TIMEOUT_MS);

      channel.port1.onmessage = (event) => {
        window.clearTimeout(timeout);
        resolve(event?.data || { ok: false, reason: "missing_service_worker_response" });
      };

      worker.postMessage(
        {
          type: "AFRIMED_SHOW_NOTIFICATION_TEST",
          title: "AfriMed™ service worker notification test",
          options: getNotificationDiagnosticOptions(
            "Service worker notifications are working for this device.",
            { diagnosticType: "service_worker_notification" }
          ),
        },
        [channel.port2]
      );
    });

    pushDebug("service worker notification diagnostic result", result);
    return {
      permission,
      scope: registration?.scope || null,
      ...result,
    };
  } catch (error) {
    pushDebug("service worker notification diagnostic failure", {
      message: error?.message || String(error || "unknown_error"),
    });
    return {
      ok: false,
      permission,
      reason: "service_worker_notification_failed",
      detail: error?.message || String(error || "unknown_error"),
    };
  }
}

async function postMessageToActiveServiceWorker(message, { timeoutMs = SERVICE_WORKER_DIAGNOSTIC_TIMEOUT_MS } = {}) {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return {
      ok: false,
      reason: "service_worker_unavailable",
    };
  }

  const registration = await navigator.serviceWorker.ready;
  const worker = registration?.active || navigator.serviceWorker.controller;
  if (!worker) {
    return {
      ok: false,
      reason: "service_worker_not_active",
      scope: registration?.scope || null,
    };
  }

  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timeout = window.setTimeout(() => {
      channel.port1.onmessage = null;
      resolve({
        ok: false,
        reason: "service_worker_message_timeout",
        scope: registration?.scope || null,
      });
    }, timeoutMs);

    channel.port1.onmessage = (event) => {
      window.clearTimeout(timeout);
      resolve({
        scope: registration?.scope || null,
        ...(event?.data || { ok: false, reason: "missing_service_worker_response" }),
      });
    };

    worker.postMessage(message, [channel.port2]);
  });
}

export async function readServiceWorkerPushDiagnostics() {
  try {
    const result = await postMessageToActiveServiceWorker({
      type: "AFRIMED_GET_PUSH_DIAGNOSTICS",
    });
    pushDebug("service worker push diagnostics read", {
      ok: Boolean(result?.ok),
      count: Array.isArray(result?.events) ? result.events.length : 0,
      reason: result?.reason || null,
    });
    return {
      ok: Boolean(result?.ok),
      events: Array.isArray(result?.events) ? result.events : [],
      reason: result?.reason || null,
      detail: result?.detail || null,
      scope: result?.scope || null,
    };
  } catch (error) {
    pushDebug("service worker push diagnostics read failed", {
      message: error?.message || String(error || "unknown_error"),
    });
    return {
      ok: false,
      events: [],
      reason: "service_worker_diagnostics_read_failed",
      detail: error?.message || String(error || "unknown_error"),
    };
  }
}

function installForegroundMessageHandler(messaging, onMessage, serviceWorkerRegistration) {
  if (foregroundMessageUnsubscribe || typeof window === "undefined") {
    pushDebug("foreground handler skipped", {
      reason: foregroundMessageUnsubscribe ? "already_installed" : "window_unavailable",
    });
    return;
  }
  if (!messaging || typeof onMessage !== "function") {
    pushDebug("foreground handler unavailable", {
      hasMessaging: Boolean(messaging),
      hasOnMessage: typeof onMessage === "function",
    });
    return;
  }

  pushDebug("foreground handler installing", {
    hasServiceWorkerRegistration: Boolean(serviceWorkerRegistration),
    canShowNotification: Boolean(serviceWorkerRegistration?.showNotification),
  });
  foregroundMessageUnsubscribe = onMessage(messaging, (payload) => {
    const summary = summarizePayload(payload);
    pushDebug("foreground payload received", summary);
    emitPushDiagnosticEvent({
      phase: "foreground_payload_received",
      ...summary,
    });
    if (!("Notification" in window) || window.Notification.permission !== "granted") {
      pushDebug("foreground notification skipped", {
        hasNotificationApi: "Notification" in window,
        permission: window.Notification?.permission || "unavailable",
      });
      emitPushDiagnosticEvent({
        phase: "foreground_notification_skipped",
        reason: "permission_not_granted",
        hasNotificationApi: "Notification" in window,
        permission: window.Notification?.permission || "unavailable",
        ...summary,
      });
      return;
    }

    const title = payload?.notification?.title || "AfriMed™";
    const body = payload?.notification?.body || "You have a new AfriMed™ notification.";
    const url = resolvePushClickUrl(payload);
    const notificationOptions = {
      body,
      icon: AFRIMED_PUSH_ICON,
      badge: AFRIMED_PUSH_ICON,
      data: {
        ...(payload?.data || {}),
        url,
      },
      tag:
        payload?.data?.type ||
        payload?.messageId ||
        `afrimed-push-${Date.now()}`,
    };

    if (serviceWorkerRegistration?.showNotification) {
      pushDebug("foreground showNotification attempted", {
        title,
        type: payload?.data?.type || null,
        source: payload?.data?.source || null,
      });
      emitPushDiagnosticEvent({
        phase: "foreground_show_notification_attempted",
        title,
        ...summary,
      });
      serviceWorkerRegistration.showNotification(title, notificationOptions)
        .then(() => {
          pushDebug("foreground showNotification success", {
            title,
            type: payload?.data?.type || null,
          });
          emitPushDiagnosticEvent({
            phase: "foreground_show_notification_success",
            title,
            ...summary,
          });
        })
        .catch((error) => {
          pushDebug("foreground showNotification failure", {
            message: error?.message || String(error || "unknown_error"),
          });
          emitPushDiagnosticEvent({
            phase: "foreground_show_notification_failure",
            title,
            detail: error?.message || String(error || "unknown_error"),
            ...summary,
          });
          try {
            pushDebug("foreground new Notification fallback attempted", { title });
            const fallbackNotification = new window.Notification(title, notificationOptions);
            fallbackNotification.onclick = () => {
              window.focus?.();
              if (url) window.location.assign(url);
              fallbackNotification.close();
            };
            pushDebug("foreground new Notification fallback success", { title });
            emitPushDiagnosticEvent({
              phase: "foreground_new_notification_success",
              title,
              ...summary,
            });
          } catch (fallbackError) {
            pushDebug("foreground new Notification fallback failure", {
              message: fallbackError?.message || String(fallbackError || "unknown_error"),
            });
            emitPushDiagnosticEvent({
              phase: "foreground_new_notification_failure",
              title,
              detail: fallbackError?.message || String(fallbackError || "unknown_error"),
              ...summary,
            });
          }
        });
      return;
    }

    try {
      pushDebug("foreground new Notification attempted", {
        title,
        type: payload?.data?.type || null,
      });
      const notification = new window.Notification(title, notificationOptions);
      notification.onclick = () => {
        window.focus?.();
        if (url) window.location.assign(url);
        notification.close();
      };
      pushDebug("foreground new Notification success", { title });
      emitPushDiagnosticEvent({
        phase: "foreground_new_notification_success",
        title,
        ...summary,
      });
    } catch (error) {
      pushDebug("foreground new Notification failure", {
        message: error?.message || String(error || "unknown_error"),
      });
      emitPushDiagnosticEvent({
        phase: "foreground_new_notification_failure",
        title,
        detail: error?.message || String(error || "unknown_error"),
        ...summary,
      });
    }
  });
  pushDebug("foreground handler installed");
}

export async function registerFirebasePushDevice({ forceRefresh = false } = {}) {
  if (typeof window === "undefined" || !("Notification" in window)) {
    pushDebug("registration unavailable", {
      hasWindow: typeof window !== "undefined",
      hasNotificationApi: typeof window !== "undefined" && "Notification" in window,
    });
    return {
      ok: false,
      registered: false,
      reason: "browser_notifications_unavailable",
    };
  }

  const permission =
    Notification.permission === "granted"
      ? "granted"
      : await Notification.requestPermission();
  pushDebug("permission state", { permission });

  if (permission !== "granted") {
    await callFunctionRaw("push-device-register", {
      permissionStatus: permission,
      provider: "fcm",
      platform: "web",
    });
    return { ok: false, registered: false, reason: permission };
  }

  const { config, vapidKey, missing } = getFirebasePushConfig();
  pushDebug("firebase config check", {
    configured: missing.length === 0,
    missing,
    hasVapidKey: Boolean(vapidKey),
  });
  if (missing.length) {
    await callFunctionRaw("push-device-register", {
      permissionStatus: permission,
      provider: "fcm",
      platform: "web",
    });
    return {
      ok: false,
      registered: false,
      reason: "missing_firebase_config",
      missing,
    };
  }

  const { initializeApp, getApps, getMessaging, getToken, deleteToken, isSupported, onMessage } =
    await loadFirebaseMessaging();
  const supported = await isSupported().catch(() => false);
  pushDebug("firebase messaging support", { supported });
  if (!supported) {
    return {
      ok: false,
      registered: false,
      reason: "firebase_messaging_unsupported",
    };
  }

  const app = getApps().length ? getApps()[0] : initializeApp(config);
  pushDebug("firebase app ready", { reusedExistingApp: getApps().length > 1 });
  const serviceWorkerRegistration = await registerServiceWorker(config);
  const messaging = getMessaging(app);
  installForegroundMessageHandler(messaging, onMessage, serviceWorkerRegistration);

  if (forceRefresh && typeof deleteToken === "function") {
    pushDebug("fcm token force refresh attempted");
    try {
      await deleteToken(messaging);
      pushDebug("fcm token force refresh cache cleared");
    } catch (error) {
      pushDebug("fcm token force refresh cache clear failed", {
        message: error?.message || String(error || "unknown_error"),
      });
    }
  }

  let token = "";
  try {
    token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration,
    });
  } catch (error) {
    const summary = summarizePushError(error);
    pushDebug("fcm token failure", {
      code: summary.code || null,
      reason: summary.reason,
      message: summary.message,
    });
    await callFunctionRaw("push-device-register", {
      permissionStatus: permission,
      provider: "fcm",
      platform: "web",
    }).catch((registerError) => {
      pushDebug("fcm token failure status persist failed", {
        message: registerError?.message || String(registerError || "unknown_error"),
      });
    });
    return {
      ok: false,
      registered: false,
      reason: summary.reason,
      detail: summary.message,
    };
  }
  pushDebug("fcm token result", { tokenPresent: Boolean(token) });

  if (!token) {
    return { ok: false, registered: false, reason: "missing_fcm_token" };
  }

  return callFunctionRaw("push-device-register", {
    token,
    permissionStatus: permission,
    provider: "fcm",
    platform: "web",
    userAgent: navigator.userAgent,
  });
}

export async function sendFirebasePushTest({ role = "", deviceId = "" } = {}) {
  pushDebug("push-test request start", {
    role: role || null,
    hasDeviceId: Boolean(deviceId),
  });
  const result = await callFunctionRaw("push-test", {
    provider: "fcm",
    platform: "web",
    role,
    deviceId,
  });
  pushDebug("push-test response", {
    pushSent: Boolean(result?.pushSent),
    fallbackInApp: Boolean(result?.fallbackInApp),
    deviceId: result?.deviceId || null,
    firebaseResponseName: result?.firebaseResponse?.name || null,
    reason: result?.reason || null,
  });
  return result;
}

export async function sendFirebaseBackgroundDiagnostic({
  role = "",
  deviceId = "",
  payloadMode = "notification_plus_data",
  delaySeconds = 0,
} = {}) {
  pushDebug("push-test-background-proof request start", {
    role: role || null,
    hasDeviceId: Boolean(deviceId),
    payloadMode,
    delaySeconds,
  });
  const result = await callFunctionRaw("push-test-background-proof", {
    provider: "fcm",
    platform: "web",
    role,
    deviceId,
    payloadMode,
    delaySeconds,
  });
  pushDebug("push-test-background-proof response", {
    pushSent: Boolean(result?.pushSent),
    fallbackInApp: Boolean(result?.fallbackInApp),
    targetDeviceId: result?.targetDeviceId || result?.deviceId || null,
    firebaseResponseName: result?.firebaseResponse?.name || null,
    payloadMode: result?.payloadMode || payloadMode,
    delaySeconds: result?.delaySeconds || delaySeconds,
    reason: result?.reason || null,
  });
  return result;
}
